import { normalizeApiBase } from "../api";
import { CsrfTokenError, csrfTokenCacheFor } from "../csrf";
import { isPairingCode, isRenderableQrDataUrl } from "../whatsapp-qr";
import type { PairingState, PairingView } from "../whatsapp-qr";

/**
 * Slice-local client for the `/v1/whatsapp` surface. It mirrors the central
 * control-plane client's cookie + CSRF + bounded-fetch discipline but stays in
 * this slice so the shared `api.ts` is untouched. The browser only ever calls
 * the API here; it never reaches the wa-bridge and never receives a token.
 */

export class WhatsAppApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "WhatsAppApiError";
  }
}

const REQUEST_TIMEOUT_MS = 15_000;

const PAIRING_STATES: readonly PairingState[] = [
  "awaiting_qr",
  "qr_ready",
  "pairing",
  "connected",
  "logged_out",
];

/** The calls the wizard needs. Injected so tests can supply a fake. */
export interface WhatsAppWizardApi {
  recordConsent(signal?: AbortSignal): Promise<{ connectionId: string }>;
  getPairing(connectionId: string, signal?: AbortSignal): Promise<PairingView>;
  refreshPairing(
    connectionId: string,
    signal?: AbortSignal,
  ): Promise<PairingView>;
  /** Starts a phone-number code flow; the phone is cleaned digits only. */
  startPairingCode(
    connectionId: string,
    phoneNumber: string,
    signal?: AbortSignal,
  ): Promise<PairingView>;
}

/** Builds the real API-backed client bound to an API origin. */
export function createWhatsAppApi(apiBase: string): WhatsAppWizardApi {
  return {
    async recordConsent(signal) {
      const response = await request(apiBase, "/v1/whatsapp/connections", {
        method: "POST",
        body: JSON.stringify({ accepted: true }),
        signal,
      });
      const payload: unknown = await response.json();
      const connectionId = readConnectionId(payload);
      return { connectionId };
    },
    async getPairing(connectionId, signal) {
      const response = await request(apiBase, pairingPath(connectionId), {
        signal,
      });
      return parsePairingView(await response.json());
    },
    async refreshPairing(connectionId, signal) {
      const response = await request(
        apiBase,
        `${pairingPath(connectionId)}/refresh`,
        { method: "POST", body: JSON.stringify({}), signal },
      );
      return parsePairingView(await response.json());
    },
    async startPairingCode(connectionId, phoneNumber, signal) {
      const response = await request(
        apiBase,
        `${pairingPath(connectionId)}-code`,
        { method: "POST", body: JSON.stringify({ phoneNumber }), signal },
      );
      return parsePairingView(await response.json());
    },
  };
}

function pairingPath(connectionId: string): string {
  return `/v1/whatsapp/connections/${encodeURIComponent(connectionId)}/pairing`;
}

function readConnectionId(payload: unknown): string {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "connectionId" in payload &&
    typeof payload.connectionId === "string"
  ) {
    return payload.connectionId;
  }
  throw new WhatsAppApiError("The WhatsApp connection response was invalid.");
}

/**
 * Validates the public pairing view. A QR that is not a strict inline PNG is
 * dropped rather than bound to an image element, closing the same gap the API
 * already enforces.
 */
export function parsePairingView(payload: unknown): PairingView {
  if (typeof payload !== "object" || payload === null) {
    throw new WhatsAppApiError("The WhatsApp pairing response was invalid.");
  }
  const record = payload as Record<string, unknown>;
  const state = record.state;
  if (
    typeof state !== "string" ||
    !PAIRING_STATES.includes(state as PairingState)
  ) {
    throw new WhatsAppApiError("The WhatsApp pairing state was invalid.");
  }
  const view: PairingView = { state: state as PairingState };
  const next: {
    -readonly [Key in keyof PairingView]: PairingView[Key];
  } = { ...view };
  if (isRenderableQrDataUrl(record.qrDataUrl)) {
    next.qrDataUrl = record.qrDataUrl;
  }
  if (typeof record.expiresAt === "string") {
    next.expiresAt = record.expiresAt;
  }
  if (isPairingCode(record.pairingCode)) {
    next.pairingCode = record.pairingCode;
  }
  return next;
}

async function request(
  apiBase: string,
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const origin = normalizeApiBase(apiBase);
  if (!origin) {
    throw new WhatsAppApiError("The WhatsApp API origin is invalid.");
  }
  const method = (init.method ?? "GET").toUpperCase();
  const mutation =
    method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
  const send = (csrf: string | null): Promise<Response> =>
    boundedFetch(`${origin}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(csrf ? { "csrf-token": csrf } : {}),
        ...init.headers,
      },
    });
  const response = mutation
    ? await sendWithCsrf(origin, send)
    : await send(null);
  if (!response.ok) {
    throw new WhatsAppApiError(
      `The WhatsApp API returned ${response.status}.`,
      response.status,
    );
  }
  return response;
}

/**
 * The token cache is shared with the control-plane client and the memory graph
 * (csrf.ts), so the wizard reuses whatever token the page already holds. Its
 * failures are re-thrown as WhatsApp errors so this slice's callers keep seeing
 * one error type.
 */
async function sendWithCsrf(
  origin: string,
  perform: (csrf: string) => Promise<Response>,
): Promise<Response> {
  try {
    return await csrfTokenCacheFor(origin).send(perform);
  } catch (error) {
    if (error instanceof CsrfTokenError) {
      throw new WhatsAppApiError(error.message, error.status);
    }
    throw error;
  }
}

async function boundedFetch(
  input: string,
  init: RequestInit,
): Promise<Response> {
  const controller = new AbortController();
  const external = init.signal;
  if (external?.aborted) controller.abort(external.reason);
  const forward = () => controller.abort(external?.reason);
  external?.addEventListener("abort", forward, { once: true });
  const timer = globalThis.setTimeout(
    () => controller.abort(new WhatsAppApiError("The WhatsApp API timed out.")),
    REQUEST_TIMEOUT_MS,
  );
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    globalThis.clearTimeout(timer);
    external?.removeEventListener("abort", forward);
  }
}
